import * as React from 'react';
import { useTheme } from '@mui/material';
import MuiTabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Stack from '@mui/material/Stack';
import { useLocation, useSearchParams } from 'react-router-dom';
import * as Device from 'react-device-detect';
import { Capacitor } from '@capacitor/core';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';

import { SearchEventForm, SearchSpotForm, SearchMemberForm } from '@components';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  style?: React.CSSProperties;
}

function samePageLinkNavigation(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
  if (
    event.defaultPrevented ||
    event.button !== 0 || // ignore everything but left-click
    event.metaKey ||
    event.ctrlKey ||
    event.altKey ||
    event.shiftKey
  ) {
    return false;
  }
  return true;
}

const SearchTabPanel = React.memo((props: TabPanelProps) => {
  const { children, value, index, style, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`search-tabpanel-${index}`}
      aria-labelledby={`search-tab-${index}`}
      style={{ ...style }}
      {...other}
    >
      {value === index && children}
    </div>
  );
});

interface LinkTabProps {
  label?: string;
  href: string;
  selected?: boolean;
}

function LinkTab(props: Readonly<LinkTabProps>) {
  return (
    <Tab
      component="a"
      onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
      }}
      aria-current={props.selected && 'page'}
      {...props}
    />
  );
}

interface SearchCardProps {
  children: React.ReactNode;
}

export const SearchCard = (props: Readonly<SearchCardProps>) => {
  const { children } = props;
  const theme = useTheme();

  return <Stack sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 1 }}>{children}</Stack>;
};

export const MobileSearchHeader = React.memo(() => {
  const [tab, setTab] = React.useState(0);
  const [expanded, setExpanded] = React.useState(false);
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const validTabs: { [key: number]: string } = {
    0: 'spots',
    1: 'events',
    2: 'members',
  };

  const handleChangeTab = (event: React.SyntheticEvent, newTab: number) => {
    const currentPath = location.pathname + (location.search ?? '');
    // event.type can be equal to focus with selectionFollowsFocus.
    if (
      event.type !== 'click' ||
      (event.type === 'click' &&
        samePageLinkNavigation(event as React.MouseEvent<HTMLAnchorElement, MouseEvent>))
    ) {
      if (Object.keys(validTabs).includes(newTab.toString())) {
        const location = searchParams.get('location');

        setSearchParams(
          { search: validTabs[newTab], page: '1', ...(location && { location }) },
          {
            state: { from: currentPath },
            replace: true,
            flushSync: true,
            viewTransition: true,
          }
        );
      }
    }
  };

  // fallback to spots if no search param
  React.useEffect(() => {
    const currentPath = location.pathname + (location.search ?? '');
    const { search } = location;

    if (!search) {
      const lastSearch = localStorage.getItem('last-search');

      if (lastSearch) {
        setSearchParams(lastSearch, {
          state: { from: lastSearch },
          replace: true,
          viewTransition: true,
          preventScrollReset: true,
        });
      } else {
        setSearchParams(
          { search: 'spots', page: '1' },
          {
            state: { from: currentPath },
            replace: true,
            viewTransition: true,
            preventScrollReset: true,
          }
        );
      }
    }
  }, [location]);

  // set tab based on search param
  React.useEffect(() => {
    const search = searchParams.get('search');
    const page = searchParams.get('page');

    if (search) {
      const idx = Object.values(validTabs).indexOf(search);

      if (idx !== -1) {
        setTab(idx);
      } else {
        setSearchParams(
          { search: 'spots', page: page ?? '1' },
          { state: { from: 'searchPage-1' }, replace: true, viewTransition: true, preventScrollReset: true }
        );
      }
    }
  }, [searchParams]);

  React.useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      Keyboard.setResizeMode({
        mode: KeyboardResize.Body,
      });

      return () => {
        Keyboard.setResizeMode({
          mode: KeyboardResize.Native,
        });
      };
    }
  }, [Keyboard]);

  // detect if scrolling down medium, if yes, hide the keyboard if open
  // React.useEffect(() => {
  //   // but not with scroll ref as we don't have it yet
  //   const onScroll = (event: Event) => {
  //     console.log(`[{event}]:`, event);
  //     Keyboard.hide();
  //   };

  //   window.addEventListener('scroll', onScroll, { passive: true });

  //   return () => {
  //     window.removeEventListener('scroll', onScroll);
  //   };
  // }, []);

  if (Device.isDesktop) {
    return null;
  }

  return (
    <Stack width="100%">
      <SearchTabPanel
        value={tab}
        index={0}
        style={{ padding: theme.spacing(Capacitor.isNativePlatform() ? 1 : 2, 0, 0, 0), minHeight: '100%' }}
      >
        <SearchSpotForm expanded={expanded} setExpanded={setExpanded} />
      </SearchTabPanel>
      <SearchTabPanel
        value={tab}
        index={1}
        style={{ padding: theme.spacing(Capacitor.isNativePlatform() ? 1 : 2, 0, 0, 0), minHeight: '100%' }}
      >
        <SearchEventForm expanded={expanded} setExpanded={setExpanded} />
      </SearchTabPanel>
      <SearchTabPanel
        value={tab}
        index={2}
        style={{ padding: theme.spacing(Capacitor.isNativePlatform() ? 1 : 2, 0, 0, 0), minHeight: '100%' }}
      >
        <SearchMemberForm expanded={expanded} setExpanded={setExpanded} />
      </SearchTabPanel>
      <MuiTabs
        value={tab}
        onChange={handleChangeTab}
        aria-label="search tabs"
        centered
        indicatorColor="primary"
        textColor="inherit"
        variant="fullWidth"
      >
        <LinkTab label="Spots" href="/search?search=spots" selected={tab === 0} />
        <LinkTab label="Events" href="/search?search=events" selected={tab === 1} />
        <LinkTab label="Members" href="/search?search=members" selected={tab === 2} />
      </MuiTabs>
    </Stack>
  );
});
